if (!customElements.get('scroll-dots-v2')) {
  class ScrollDotsV2 extends HTMLElement {
    /** @type {AbortController | null} */
    #abort = null;

    /** @type {number} */
    #frame = 0;

    connectedCallback() {
      const trackElement = this.querySelector('[data-scroll-track]');
      if (!(trackElement instanceof HTMLElement)) return;

      this.#abort = new AbortController();
      const { signal } = this.#abort;
      const dotElements = [...this.querySelectorAll('[data-scroll-dot]')];

      trackElement.addEventListener(
        'scroll',
        () => {
          cancelAnimationFrame(this.#frame);
          this.#frame = requestAnimationFrame(() => this.#update(trackElement, dotElements));
        },
        { passive: true, signal },
      );

      dotElements.forEach(
        /** @param {Element} dotElement @param {number} index */
        (dotElement, index) => {
          dotElement.addEventListener('click', () => this.#scrollTo(trackElement, index), { signal });
        },
      );

      this.#update(trackElement, dotElements);
    }

    disconnectedCallback() {
      this.#abort?.abort();
      this.#abort = null;
      cancelAnimationFrame(this.#frame);
    }

    /** @param {HTMLElement} trackElement */
    #step(trackElement) {
      const firstItem = trackElement.firstElementChild;
      const secondItem = firstItem?.nextElementSibling;
      if (!(firstItem instanceof HTMLElement)) return 0;
      if (secondItem instanceof HTMLElement) return secondItem.offsetLeft - firstItem.offsetLeft;
      return firstItem.offsetWidth;
    }

    /** @param {HTMLElement} trackElement @param {number} index */
    #scrollTo(trackElement, index) {
      const step = this.#step(trackElement);
      const direction = getComputedStyle(trackElement).direction === 'rtl' ? -1 : 1;
      trackElement.scrollTo({ left: step * index * direction, behavior: 'smooth' });
    }

    /** @param {HTMLElement} trackElement @param {Element[]} dotElements */
    #update(trackElement, dotElements) {
      const step = this.#step(trackElement);
      if (!step || dotElements.length === 0) return;

      const scrollPosition = Math.abs(trackElement.scrollLeft);
      const maxScroll = trackElement.scrollWidth - trackElement.clientWidth;
      let activeIndex = Math.round(scrollPosition / step);
      if (maxScroll > 0 && scrollPosition >= maxScroll - 2) activeIndex = dotElements.length - 1;
      activeIndex = Math.min(Math.max(activeIndex, 0), dotElements.length - 1);

      dotElements.forEach(
        /** @param {Element} dotElement @param {number} index */
        (dotElement, index) => {
          dotElement.setAttribute('aria-current', index === activeIndex ? 'true' : 'false');
        },
      );
    }
  }

  customElements.define('scroll-dots-v2', ScrollDotsV2);
}
